import { FC, useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Layout from "../../../layout/Layout";
import CarCardComponent from "../../../components/carCard/CarCardComponent";
import LoadingComponent from "../../../components/LoadingComponent/LoadingComponent";
import AuthGateModal from "../../../components/authGateModal/AuthGateModal";
import FavoriteCarsContext from "../../../../../domain/providers/favoriteCars/FavoriteCarsContext";
import UserContext from "../../../../../domain/providers/user/UserContext";
import di from "../../../../../di/DependencyInjection";
import GetFavoriteCarsUseCase, {
    GetFavoriteCarsUseCaseName,
} from "../../../../../domain/use_cases/car/GetFavoriteCarsUseCase";
import { routes } from "../../../routes/RoutesComponent";
import { useTrackOnce } from "../../../hooks/useTrackOnce";
import "./FavoritesPageStyles.scss";

// 4 columnas x 2 filas, como en el diseño.
const POR_PAGINA = 8;

type Orden = "recientes" | "menor_precio" | "mayor_precio";
const precioDe = (car: { price: number; discount_price?: number }) => car.discount_price ?? car.price;

const FavoritesPage: FC = () => {
    const { user } = useContext(UserContext);
    const { favoriteCars } = useContext(FavoriteCarsContext);
    const [cargando, setCargando] = useState(true);
    const [busqueda, setBusqueda] = useState("");
    const [orden, setOrden] = useState<Orden>("recientes");
    const [pagina, setPagina] = useState(1);
    // El backend ya filtra, pero un vehículo puede desactivarse mientras la lista está en memoria.
    const activos = useMemo(() => favoriteCars.filter((car) => car.active !== false), [favoriteCars]);
    // "recientes" conserva el orden del backend (último guardado primero).
    const visibles = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        const filtrados = q
            ? activos.filter((car) =>
                  [car.name, car.brand?.name, car.color?.name].some((t) => t?.toLowerCase().includes(q)),
              )
            : activos;
        if (orden === "recientes") return filtrados;
        return [...filtrados].sort((a, b) => (orden === "menor_precio" ? 1 : -1) * (precioDe(a) - precioDe(b)));
    }, [activos, busqueda, orden]);
    const totalPaginas = Math.max(1, Math.ceil(visibles.length / POR_PAGINA));
    // Al quitar favoritos la última página puede desaparecer.
    const paginaActual = Math.min(pagina, totalPaginas);
    const enPagina = visibles.slice((paginaActual - 1) * POR_PAGINA, paginaActual * POR_PAGINA);

    useEffect(() => {
        if (!user) return;
        di.get<GetFavoriteCarsUseCase>(GetFavoriteCarsUseCaseName)
            .call()
            .finally(() => setCargando(false));
    }, [user]);

    useTrackOnce("favoritos_vistos", { cantidad: activos.length }, Boolean(user) && !cargando);
    useTrackOnce("pagina_vista", { pagina: "favoritos" }, Boolean(user));

    return (
        <Layout>
            <Helmet>
                <title>Mis favoritos - wcar</title>
                <meta name="robots" content="noindex" />
            </Helmet>
            {/* Sin onClose: la vista de favoritos exige sesión. */}
            {!user && <AuthGateModal origen="favorito" mensaje="Inicia sesión para ver tus vehículos favoritos" />}
            <div className="favorites_page">
                <section className="favorites_hero">
                    <div className="container favorites_hero_content">
                        <img className="favorites_hero_heart" src="/assets/favoritos/heart.svg" alt="" />
                        <div>
                            <h1>
                                Vehículos <em>Favoritos</em>
                            </h1>
                            <p>Revisa y gestiona los vehículos que marcaste como favoritos</p>
                        </div>
                    </div>
                    <img className="favorites_hero_w" src="/assets/favoritos/w.svg" alt="" />
                </section>
                <div className="favorites_body">
                    {user && cargando && <LoadingComponent />}
                    {user && !cargando && activos.length === 0 && (
                        <p>
                            Aún no tienes vehículos favoritos.{" "}
                            <Link to={routes.buyYourCar.relativePath}>Explora el catálogo</Link>
                        </p>
                    )}
                    {user && activos.length > 0 && (
                        <>
                            <div className="favorites_toolbar">
                                <label className="favorites_search">
                                    <img src="/assets/icons/search.svg" alt="" />
                                    <input
                                        type="search"
                                        placeholder="Busca por marca, modelo, color..."
                                        value={busqueda}
                                        onChange={(e) => {
                                            setBusqueda(e.target.value);
                                            setPagina(1);
                                        }}
                                    />
                                </label>
                                <label className="favorites_sort">
                                    Ordenar por:
                                    <select
                                        value={orden}
                                        onChange={(e) => {
                                            setOrden(e.target.value as Orden);
                                            setPagina(1);
                                        }}
                                    >
                                        <option value="recientes">Recientes</option>
                                        <option value="menor_precio">Menor precio</option>
                                        <option value="mayor_precio">Mayor precio</option>
                                    </select>
                                </label>
                            </div>
                            {visibles.length === 0 && <p>No hay favoritos que coincidan con tu búsqueda.</p>}
                            <div className="favorites_grid">
                                {enPagina.map((car, index) => (
                                    <CarCardComponent
                                        key={car.id}
                                        car={car}
                                        origen="favoritos"
                                        posicion={(paginaActual - 1) * POR_PAGINA + index + 1}
                                        pagina={paginaActual}
                                    />
                                ))}
                            </div>
                            {totalPaginas > 1 && (
                                <div className="favorites_pagination">
                                    <button
                                        type="button"
                                        className="prev"
                                        aria-label="Página anterior"
                                        disabled={paginaActual <= 1}
                                        onClick={() => setPagina(paginaActual - 1)}
                                    >
                                        <img src="/assets/favoritos/arrow-active.svg" alt="" />
                                    </button>
                                    <span>
                                        {paginaActual} de {totalPaginas}
                                    </span>
                                    <button
                                        type="button"
                                        aria-label="Página siguiente"
                                        disabled={paginaActual >= totalPaginas}
                                        onClick={() => setPagina(paginaActual + 1)}
                                    >
                                        <img src="/assets/favoritos/arrow-active.svg" alt="" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default FavoritesPage;
